import { useEffect, useMemo, useRef } from "react";
import clsx from "clsx";
import bellImg from "@src/shared/assets/bellImg.svg";
import s from "./NotificationsBellView.module.scss";
import { formatDateTimeForUi } from "@src/shared/lib/dayjs";

export type BellNotification = {
  id: string;
  text: string;
  createdAt?: string;
  isRead: boolean;
};

type Props = {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  items: BellNotification[];
  unreadCount: number;
  onItemClick: (id: string) => void;
  onShowAll: () => void;
  title?: string;
};

export function NotificationsBellView({
  open,
  onToggle,
  onClose,
  items,
  unreadCount,
  onItemClick,
  onShowAll,
  title = "Уведомления",
}: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  const badgeText = useMemo(() => {
    if (unreadCount <= 0) return null;
    return unreadCount > 99 ? "99+" : String(unreadCount);
  }, [unreadCount]);

  // Закрытие по клику вне
  useEffect(() => {
    if (!open) return;

    const onDocMouseDown = (e: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (e.target instanceof Node && !root.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, onClose]);

  return (
    <div className={s.root} ref={rootRef}>
      <button
        type="button"
        className={s.bellButton}
        onClick={onToggle}
        aria-label={title}
        aria-expanded={open}
      >
        <div className={s.bellIcon} aria-hidden>
          <img src={bellImg} />
        </div>

        {badgeText && <span className={s.badge}>{badgeText}</span>}
      </button>

      {open && (
        <div className={s.dropdown} role="menu" aria-label={title}>
          <div className={s.header}>
            <div className={s.title}>{title}</div>

            <div className={s.actions}>
              <button type="button" className={s.actionBtn} onClick={onClose}>
                Закрыть
              </button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className={s.empty}>Нет уведомлений</div>
          ) : (
            <div className={s.list}>
              {items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={clsx(s.item, !n.isRead && s.itemUnread)}
                  onClick={() => onItemClick(n.id)}
                >
                  <div className={s.itemTop}>
                    <div className={s.text}>{n.text}</div>
                    {!n.isRead ? <span className={s.dot} /> : null}
                  </div>

                  <div className={s.meta}>
                    {formatDateTimeForUi(n.createdAt || "")}
                  </div>
                </button>
              ))}
            </div>
          )}
          {items.length > 0 && (
            <div className={s.footer}>
              <button
                type="button"
                className={s.footerLink}
                onClick={onShowAll}
              >
                Показать все
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


